// src/components/loader.js
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { loaderDelay } from '@utils';

// === Animations ===
const move = keyframes`
  0% {
    transform: translateY(0px);
  }
  25% {
    transform: translateY(88px);
  }
  50% {
    transform: translateY(0px);
  }
  75% {
    transform: translateY(88px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const grow = keyframes`
  0%, 50%, 75%, 100% {
    transform: scaleX(0.7) scaleY(0.7);
  }
  10%, 60% {
    transform: scaleX(1) scaleY(1);
  }
  35%, 85% {
    transform: scaleX(0.4) scaleY(0.4);
  }
`;

// Full-screen overlay
const FullscreenLoader = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  pointer-events: auto;

  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: center;
  align-items: center;
  text-align: center;

  /* match your site background theme */
  background:
    radial-gradient(circle at top left,
      rgba(145, 154, 255, 0.22) 0,
      transparent 55%),
    radial-gradient(circle at bottom right,
      rgba(219, 160, 255, 0.20) 0,
      transparent 60%),
    var(--navy);

  transition: opacity 0.4s ease-out;
`;

// DNA wave wrapper
const WaveWrapper = styled.div`
  display: flex;
  position: relative;
  width: 360px; /* ⬅️ longer DNA line */
  height: 88px;

  .wave {
    display: flex;
    justify-content: space-between;
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    perspective: 100px;
  }

  .wave > div {
    position: relative;
    width: 16px;
    height: 16px;
    border-radius: 100%;
  }

  .wave > div::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background-color: #ffffff;
  }

  /* top wave color: accent pink/purple from your theme */
  .top-wave > div::before {
    background: linear-gradient(
      135deg,
      #f3a8c9,
      #d9b4ff
    );
  }

  /* bottom wave color: softer desaturated tone */
  .bottom-wave > div::before {
    background: linear-gradient(
      135deg,
      #b6c4ff,
      #ecd9ff
    );
  }

  .top-wave > div {
    animation: ${move} 3s ease-in-out infinite reverse;
  }

  .top-wave > div::before {
    animation: ${grow} 3s linear infinite reverse;
  }

  .bottom-wave > div {
    animation: ${move} 3s ease-in-out infinite;
  }

  .bottom-wave > div::before {
    animation: ${grow} 3s linear infinite;
  }

  /* delays, same pattern as original */
  .wave > div:nth-child(10) { animation-delay: 0s; }
  .wave > div:nth-child(9)  { animation-delay: -0.1s; }
  .wave > div:nth-child(8)  { animation-delay: -0.2s; }
  .wave > div:nth-child(7)  { animation-delay: -0.3s; }
  .wave > div:nth-child(6)  { animation-delay: -0.4s; }
  .wave > div:nth-child(5)  { animation-delay: -0.5s; }
  .wave > div:nth-child(4)  { animation-delay: -0.6s; }
  .wave > div:nth-child(3)  { animation-delay: -0.7s; }
  .wave > div:nth-child(2)  { animation-delay: -0.8s; }
  .wave > div:nth-child(1)  { animation-delay: -0.9s; }

  .bottom-wave > div:nth-child(10) { animation-delay: 0.75s; }
  .bottom-wave > div:nth-child(9)  { animation-delay: 0.65s; }
  .bottom-wave > div:nth-child(8)  { animation-delay: 0.55s; }
  .bottom-wave > div:nth-child(7)  { animation-delay: 0.45s; }
  .bottom-wave > div:nth-child(6)  { animation-delay: 0.35s; }
  .bottom-wave > div:nth-child(5)  { animation-delay: 0.25s; }
  .bottom-wave > div:nth-child(4)  { animation-delay: 0.15s; }
  .bottom-wave > div:nth-child(3)  { animation-delay: 0.05s; }
  .bottom-wave > div:nth-child(2)  { animation-delay: -0.05s; }
  .bottom-wave > div:nth-child(1)  { animation-delay: -0.15s; }
`;

const Caption = styled.div`
  font-family: var(--font-mono);
  font-size: var(--fz-sm);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--light-slate);
  opacity: 0.85;
`;

// Loader component
const Loader = ({ finishLoading }) => {
  useEffect(() => {
    // lock scroll like original
    document.body.classList.add('hidden');

    const MIN_DURATION = 3000; // ⬅️ at least 3 seconds
    const duration = Math.max(loaderDelay || 0, MIN_DURATION);

    const timeout = setTimeout(() => {
      document.body.classList.remove('hidden');
      finishLoading();
    }, duration);

    return () => {
      clearTimeout(timeout);
      document.body.classList.remove('hidden');
    };
  }, [finishLoading]);

  return (
    <FullscreenLoader>
      <WaveWrapper>
        <div className="wave top-wave">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={`top-${i}`} />
          ))}
        </div>
        <div className="wave bottom-wave">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={`bottom-${i}`} />
          ))}
        </div>
      </WaveWrapper>

      <Caption>Entering JZHAO&apos;s space</Caption>
    </FullscreenLoader>
  );
};

Loader.propTypes = {
  finishLoading: PropTypes.func.isRequired,
};

export default Loader;
